<template>
    <div class="px-4 sm:px-6 lg:px-3">
        <div v-if="isTrialing" class="p-3 mb-4 text-center bg-green-500">
            <p class="text-sm italic font-medium leading-6 text-white">
                You are on a free trial for {{ trialDays }} more days.
            </p>
        </div>
        <form method="POST" :action="paymentRoute" id="payment-form">
            <input type="hidden" name="token" id="token" :value="token">
            <input type="hidden" name="stripe_id" :value="getStripeId"/>
            <div class="bg-white shadow sm:rounded-md sm:overflow-hidden">
                <div class="px-4 py-6 sm:p-6">
                    <section aria-labelledby="plan-heading">
                        <div class="flex justify-center w-full">
                            <div class="flex items-center">
                                <fieldset class="grid grid-cols-2 p-1 text-xs font-semibold leading-5 text-center rounded-lg gap-x-1 ring-1 ring-inset ring-gray-200">
                                    <legend class="sr-only">Payment frequency</legend>
                                    <label class="cursor-pointer rounded-lg px-2.5 py-2"
                                        :class="[billingInterval === 'month' ? 'bg-blue-600 text-white' : '']"
                                    >
                                        <input type="radio" @change="toggleCurrentPlan" name="frequency" value="monthly" class="sr-only">
                                        <span class="font-bold text-md">Monthly</span>
                                    </label>
                                    <label class="cursor-pointer rounded-lg px-2.5 py-2"
                                        :class="[billingInterval === 'year' ? 'bg-blue-600 text-white' : '']"
                                    >
                                        <input type="radio" @change="toggleCurrentPlan" name="frequency" value="annually" class="sr-only">
                                        <span class="font-bold text-md">Annually (2 Months Free)</span>
                                    </label>
                                </fieldset>
                            </div>
                        </div>
                        <fieldset>
                            <div class="grid grid-cols-1 mt-4 gap-y-6 sm:grid-cols-3 sm:gap-x-6">
                                <label
                                    class="flex items-start p-4 bg-white border rounded-lg shadow-sm cursor-pointer focus:outline-none"
                                    :class="[currentTier3.identifier === plan.identifier ? 'border-blue-600 ring-2 ring-blue-600' : 'border-gray-300']"
                                >
                                    <input type="radio" name="plan" v-model="plan_identifier" :value="currentTier3.identifier" @change="changePlan($event)" class="sr-only">
                                    <span class="flex flex-1">
                                        <span class="flex flex-col w-full">
                                            <span class="flex justify-between w-full font-medium text-gray-900 text-md">
                                                <p>Enterprise</p>
                                                <svg v-if="currentTier3.identifier === plan.identifier" class="w-5 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                                                </svg>
                                            </span>
                                            <span class="flex items-center mt-1 text-sm text-gray-500">Best for industry executives looking for a full picture of the industry</span>
                                            <span  v-if="billingInterval === 'month'" class="mt-6 italic text-red-600 line-through text-md">$399.00 /month</span>
                                            <span  v-else-if="billingInterval === 'year'" class="mt-6 italic text-red-600 line-through text-md">$4,788.00 /year</span>
                                            <span class="mt-2 font-bold text-gray-900 text-md">{{ showPlanPrice(currentTier3.price) }}/ {{ billingInterval }}</span>
                                            <span class="justify-start mt-6">
                                                <ul role="list" class="space-y-3 text-sm leading-6 text-gray-600">
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Surgeon Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Hospital Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Surgery Center Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Sales Channel Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Manufacturer Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Health Network Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Team Collaboration
                                                    </li>
                                                </ul>
                                            </span>
                                        </span>
                                    </span>
                                    <span class="absolute border-2 rounded-lg pointer-events-none -inset-px" aria-hidden="true"></span>
                                </label>
                                <label
                                    class="flex items-start p-4 bg-blue-600 border rounded-lg shadow-sm cursor-pointer focus:outline-none"
                                    :class="[currentTier2.identifier === plan.identifier ? 'border-blue-600 ring-2 ring-blue-600' : 'border-gray-300']"
                                >
                                    <input type="radio" name="plan" v-model="plan_identifier" :value="currentTier2.identifier" @change="changePlan($event)" class="sr-only">
                                    <span class="flex object-top">
                                        <span class="flex flex-col w-full">
                                            <span class="flex justify-between w-full font-medium text-white text-md">
                                                <p>Premium</p>
                                                <svg v-if="currentTier2.identifier === plan.identifier" class="w-5 h-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                                                </svg>
                                            </span>
                                            <span class="flex items-center mt-1 text-sm text-white">Best for sales managers looking to expand their distribution network</span>
                                            <span  v-if="billingInterval === 'month'" class="mt-6 italic text-white line-through text-md">199.00 /month</span>
                                            <span  v-else-if="billingInterval === 'year'" class="mt-6 italic text-white line-through text-md">$2,388.00 /year</span>
                                            <span class="mt-2 font-bold text-white text-md">{{ showPlanPrice(currentTier2.price) }}/ {{ billingInterval }}</span>
                                            <span class="justify-start mt-6">
                                                <ul role="list" class="space-y-3 text-sm leading-6 text-gray-600">
                                                    <li class="flex text-white gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Surgeon Profiles
                                                    </li>
                                                    <li class="flex text-white gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Hospital Profiles
                                                    </li>
                                                    <li class="flex text-white gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Surgery Center Profiles
                                                    </li>
                                                    <li class="flex text-white gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Sales Channel Profiles
                                                    </li>
                                                    <li class="flex text-white gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Manufacturer Profiles
                                                    </li>
                                                </ul>
                                            </span>
                                        </span>
                                    </span>
                                    <span class="absolute border-2 rounded-lg pointer-events-none -inset-px" aria-hidden="true"></span>
                                </label>
                                <label
                                    class="flex items-start p-4 bg-white border rounded-lg shadow-sm cursor-pointer focus:outline-none"
                                    :class="[currentTier1.identifier === plan.identifier ? 'border-blue-600 ring-2 ring-blue-600' : 'border-gray-300']"
                                >
                                    <input type="radio" name="plan" v-model="plan_identifier" :value="currentTier1.identifier" @change="changePlan($event)" class="sr-only">
                                    <span class="flex ">
                                        <span class="flex flex-col object-top w-full">
                                            <span class="flex justify-between w-full font-medium text-gray-900 text-md">
                                                <p>Basic</p>
                                                <svg v-if="currentTier1.identifier === plan.identifier" class="w-5 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clip-rule="evenodd" />
                                                </svg>
                                            </span>
                                            <span class="flex items-center mt-1 text-sm text-gray-500">Best for new reps looking to supercharge their pipeline</span>
                                            <span  v-if="billingInterval === 'month'" class="mt-6 italic text-red-600 line-through text-md">$99.00 /month</span>
                                            <span  v-else-if="billingInterval === 'year'" class="mt-6 italic text-red-600 line-through text-md">$1,188.00 /year</span>
                                            <span class="mt-2 text-lg font-bold text-gray-900">{{ showPlanPrice(currentTier1.price) }}/ {{ billingInterval }}</span>
                                            <span class="justify-start mt-6">
                                                <ul role="list" class="space-y-3 text-sm leading-6 text-gray-600">
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Surgeon Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Hospital Profiles
                                                    </li>
                                                    <li class="flex gap-x-3">
                                                        <svg class="flex-none w-4 h-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                                                        </svg>
                                                        Surgery Center Profiles
                                                    </li>
                                                </ul>
                                            </span>
                                        </span>
                                    </span>
                                    <span class="absolute border-2 rounded-lg pointer-events-none -inset-px" aria-hidden="true"></span>
                                </label>
                            </div>
                        </fieldset>

                    </section>
                    <div class="pt-4 mt-4 md:flex md:justify-between md:items-end md:space-x-4">
                        <div class="flex justify-between w-full">
                            <div class="text-gray-500">
                                <span class="text-sm">Plan Price billed {{ billingType }}:</span>
                            </div>
                            <div class="text-gray-500">
                                <span class="text-md">{{ moneyToString(this.plan.price/100) }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="pt-4 mt-4 md:flex md:justify-between md:items-end md:space-x-4">
                        <div class="flex justify-end w-full">
                            <div class="text-gray-500 ">
                                <span class="text-md">x  <select id="plan-quantity" name="quantity" @change="changePlanSize($event)" v-model="quantity"
                                        class="max-w-full py-1 text-base font-medium leading-5 text-left text-gray-700 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                        <option
                                            v-for="i in 25"
                                            :key = i
                                            :value="i"  :disabled="lessThanMinimum(i)"
                                            :selected="i === quantity">{{ i }}
                                        </option>

                                    </select> seat(s)</span>
                            </div>
                        </div>
                    </div>
                    <div class="py-4 mt-4 border-b-2 md:flex md:justify-between md:items-end md:space-x-4">
                        <div>
                            <label for="promo-code" class="block text-sm font-medium text-gray-500">Promo Code</label>
                            <span class="flex items-center justify-start space-x-2">
                                <input
                                    v-model="userPromoCode"
                                    type="text"
                                    name="promo_code"
                                    id="promo-code"
                                    class="py-1 md:w-60 tw-input"
                                >
                                <a
                                    href="#"
                                    class="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-green-600 hover:bg-green-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    @click="checkPromoCode"
                                >Apply</a>
                            </span>
                            <div class="mt-2 text-sm text-red-600" v-if="error !== '' ">{{ error }}</div>
                        </div>
                        <div v-if="totalDiscount > 0">
                            <span class="text-sm text-gray-500">
                                {{discount.code}} <span class="font-medium text-green-600"> - {{ moneyToString(totalDiscount/100) }}</span>
                            </span>
                        </div>
                    </div>
                    <div class="pt-4 mt-4 md:flex md:justify-between md:items-end md:space-x-4">
                        <div class="flex justify-between w-full">
                            <div class="font-semibold text-gray-900 text-md">
                                Total:
                            </div>
                            <div class="text-gray-900 text-md">
                                {{ moneyToString(total/100) }} billed {{ billingType }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-6 shadow sm:rounded-md sm:overflow-hidden">
                <div class="p-4 bg-white">
                    <div v-if="paymentMethod" class="px-4 py-5 sm:p-6">
                        <h3 class="text-base font-semibold leading-6 text-gray-900">Payment method</h3>
                        <div class="mt-5">
                            <div class="px-6 py-5 rounded-md bg-gray-50 sm:flex sm:items-start sm:justify-between">
                                <h4 class="sr-only">{{ paymentMethod.brand }}</h4>
                                <div class="sm:flex sm:items-start">
                                    <svg class="w-auto h-8 sm:h-6 sm:flex-shrink-0" viewBox="0 0 36 24" aria-hidden="true">
                                        <rect width="36" height="24" fill="#224DBA" rx="4" />
                                        <path fill="#fff" d="M10.925 15.673H8.874l-1.538-6c-.073-.276-.228-.52-.456-.635A6.575 6.575 0 005 8.403v-.231h3.304c.456 0 .798.347.855.75l.798 4.328 2.05-5.078h1.994l-3.076 7.5zm4.216 0h-1.937L14.8 8.172h1.937l-1.595 7.5zm4.101-5.422c.057-.404.399-.635.798-.635a3.54 3.54 0 011.88.346l.342-1.615A4.808 4.808 0 0020.496 8c-1.88 0-3.248 1.039-3.248 2.481 0 1.097.969 1.673 1.653 2.02.74.346 1.025.577.968.923 0 .519-.57.75-1.139.75a4.795 4.795 0 01-1.994-.462l-.342 1.616a5.48 5.48 0 002.108.404c2.108.057 3.418-.981 3.418-2.539 0-1.962-2.678-2.077-2.678-2.942zm9.457 5.422L27.16 8.172h-1.652a.858.858 0 00-.798.577l-2.848 6.924h1.994l.398-1.096h2.45l.228 1.096h1.766zm-2.905-5.482l.57 2.827h-1.596l1.026-2.827z" />
                                    </svg>
                                    <div class="mt-3 sm:mt-0 sm:ml-4">
                                        <div class="text-sm font-medium text-gray-900">Ending with {{ paymentMethod.last4 }}</div>
                                        <div class="mt-1 text-sm text-gray-600 sm:flex sm:items-center">
                                            <div>Expires {{ paymentMethod.exp_month }} / {{ paymentMethod.exp_year}}</div>
                                            <span class="hidden sm:mx-2 sm:inline" aria-hidden="true"></span>
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-4 sm:mt-0 sm:ml-6 sm:flex-shrink-0">
                                    <button type="button" @click="redirectToPaymentMethod" class="inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white rounded-md shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">Edit</button>
                                </div>
                            </div>
                            <div class="flex justify-end w-full">
                                <button type="submit" :data-secret="intent.client_secret" id="card-button"
                                    class="inline-flex items-center mt-4 justify-self-end tw-button-green">
                                    <svg id="card-button-icon" class="hidden w-5 h-5 mr-3 -ml-1 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none"
                                        viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                        </path>
                                    </svg>
                                    Update Subscription
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </div>

</template>
<script>
export default {
    props: {
        tierThreePlans: {
            type: [Object, Array],
            required: true
        },
        tierTwoPlans: {
            type: [Object, Array],
            required: true
        },
        tierOnePlans: {
            type: [Object, Array],
            required: true
        },
        user: {
            type: [Object, Array],
            required: true,
        },
        promoCodes: {
            type: [Object, Array],
            required: false,
        },
        intent: {
            type: [Object, Array],
            required: true,
        },
        paymentRoute: {
            type: String,
            required: true,
        },
        paymentMethod: {
            type: [Object, Array],
            required: false,
        },
        stripeSubscription: {
            type: [Object, Array],
            required: false,
        },
        subscription: {
            type: [Object, Array],
            required: true,
        },
        isTrialing: {
            type: Boolean,
            required: false,
        },
        token: {
            type: String,
            required: true,
        },
        trialDays: {
            type: [String, Number],
            required: false,
        },
        currentPlan: {
            type: [Object, Array],
            required: true,
        },
        preselectTier3: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    computed: {
        onlyOnePlan(){
            if (this.plans.length === 1) {
                return true;
            }
            return false;
        },
        codeFilled(){
            if(this.discount.code === '') {
                return false;
            }
            return true;
        },
        getStripeId() {
            return this.plan.stripe_price;
        },
        currentTier1() {
            return this.billingInterval === 'year' ?  this.tierOnePlans['yearly'] : this.tierOnePlans['monthly'];
        },
        currentTier2() {
            return this.billingInterval === 'year' ? this.tierTwoPlans['yearly'] : this.tierTwoPlans['monthly'];
        },
        currentTier3() {
            return this.billingInterval === 'year' ? this.tierThreePlans['yearly'] : this.tierThreePlans['monthly'];
        },
        billingType() {
            return this.billingInterval == 'year' ?  'yearly' : 'monthly';
        }
    },
    mounted() {
        this.checkPromoCode()
        this.updateTotal()
    },
    data() {
        const intervalKey = this.stripeSubscription.plan.interval === 'year' ? 'yearly' : 'monthly';
        const tier3Plan = this.preselectTier3 ? this.tierThreePlans[intervalKey] : null;
        const initialPlan = tier3Plan || this.currentPlan;

        return {
            billingInterval: this.stripeSubscription.plan.interval,
            total: this.stripeSubscription.plan.amount,
            userPromoCode: '',
            discount: {
                code: '',
                title: '',
                duration: '',
                duration_in_months: '',
                free_trial_days: 0,
                percent_off: 0,
                amount_off: 0,
            },
            totalDiscount: 0,
            error: '',
            plan: initialPlan,
            plan_identifier: tier3Plan ? tier3Plan.identifier : this.stripeSubscription.plan.product,
            quantity: this.stripeSubscription.quantity,
        }
    },
    methods: {
        openPaymentMethodModal () {
          OSPEvent.$emit('open-payment-method-modal')
        },
        lessThanMinimum(i){
            return i < 0;
        },
        updateTotal(){
            var newTotal = this.plan.price * this.quantity
            this.total = newTotal;
            this.updateDiscount()
            this.total = this.total - this.discount.amount_off;
        },
        checkPromoCode() {
            if ( this.stripeSubscription.discount ) {
                this.discount.code = this.stripeSubscription.discount.coupon.name;
                if(this.stripeSubscription.discount.coupon.amount_off){
                    this.discount.amount_off = this.stripeSubscription.discount.coupon.amount_off;
                }
                if(this.stripeSubscription.discount.coupon.percent_off){
                    this.discount.percent_off = this.stripeSubscription.discount.coupon.percent_off/100;
                    this.discount.amount_off = this.total * this.discount.percent_off;
                }
                this.total = this.total-this.discount.amount_off;
                return
            }
        },
        changePlanSize(event) {
            this.quantity = parseInt(event.target.value);
            this.updateTotal();
        },
        getFreeTrialEndDate() {
            var today = new Date();
            return this.discount.free_trial_days > 0 ?  new Date(today.getTime() + this.discount.free_trial_days*24*60*60*1000).toLocaleDateString("en-US"): new Date(today.getTime()  + this.defaultTrialDays*24*60*60*1000).toLocaleDateString("en-US");
        },
        moneyToString( num ) {
            const formatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            });

            return formatter.format(parseFloat(num));
        },
        updateDiscount() {
            if(this.discount.percent_off > 0) {
                this.discount.amount_off = this.plan.price * this.quantity * this.discount.percent_off;
                this.total = this.total - this.discount.amount_off;
            }
            return;
        },
        redirectToPaymentMethod() {
            return window.location = this.route('billing.payment_method');
        },
        toggleCurrentPlan() {
            if ( this.billingInterval === 'year' ) {
                this.billingInterval = 'month'
            } else {
                this.billingInterval = 'year'
            }

            switch(this.plan_identifier){
                case this.currentTier1.identifier:
                    this.plan = this.currentTier1;
                    break
                case this.currentTier2.identifier:
                    this.plan = this.currentTier2;
                    break
                case this.currentTier3.identifier:
                    this.plan = this.currentTier3;
                    break
            };
            return this.updateTotal();
        },
        currentPlanPrice() {
            return this.moneyToString(this.plan.price/ 100);
        },
        showPlanPrice(price) {
            return this.moneyToString(price/ 100);
        },
        planChecked(value) {
            if (this.plan === value) {
                return true;
            }
            return false;
        },
        monthlyCurrentPlanPrice(price) {
            if (this.billingInterval === 'year' ) {
                return this.moneyToString(price/ 1200)
            }
            return this.moneyToString(price/ 100);
        },
        changePlan(event) {
             switch(this.plan_identifier){
                case this.currentTier1.identifier:
                    this.plan = this.currentTier1;
                    break
                case this.currentTier2.identifier:
                    this.plan = this.currentTier2;
                    break
                case this.currentTier3.identifier:
                    this.plan = this.currentTier3;
                    break
            };
            this.updateTotal();
        },
    }
}
</script>
